// Shared (client + server) shape of the Transformation dashboard link that
// drives the home card and nav item. See api.transformation-dashboard.

export type TransformationAudience = "buyer" | "champion" | "learner";

export type SpaceTransformationDashboard = {
  url: string;
  audience: TransformationAudience;
  isAdmin: boolean;
  companyName: string;
};

// The dashboard lives on the canonical Transformation host or on a mindspace
// alias (`<space>.<domain>/transformation`). Anything else in the payload is a
// broken or hostile upstream and is dropped rather than rendered as a link.
const DASHBOARD_URL_RE =
  /^https:\/\/[a-z0-9_-]+\.(?:mindstone\.com|s101\.mindstone\.dev|local\.mindstone\.dev)\/(?:company\/|transformation(?:\/|$))/;

export function isSpaceTransformationDashboard(
  value: unknown,
): value is SpaceTransformationDashboard {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.url === "string" &&
    DASHBOARD_URL_RE.test(v.url) &&
    (v.audience === "buyer" || v.audience === "champion" || v.audience === "learner") &&
    typeof v.isAdmin === "boolean" &&
    typeof v.companyName === "string"
  );
}

/**
 * The engine URL tagged `from=learning` (FOX-6291, measurement only): replaces
 * any existing `from`, keeps every other param and the hash.
 */
export function withFromLearning(url: string): string {
  const parsed = new URL(url);
  parsed.searchParams.set("from", "learning");
  return parsed.toString();
}

/**
 * Everyone the engine sends a link gets the top-level nav item: leaders,
 * admins, champions and, since 4 Nov, learners (plan
 * 261104_learner-dashboard-link; Melissanthi: "We need to add a sidebar item
 * for learners"). Who the engine sends a link is the engine's own rule.
 */
export function showsTransformationNav(dashboard: SpaceTransformationDashboard | null): boolean {
  return !!dashboard;
}

/**
 * Whether the person manages cohorts in the engine (a leader, admin or
 * champion there), so their Organisation page moves to it (FOX-6291). A
 * learner-only engine link never moves it, even for someone who is a Learning
 * admin or champion: the engine gives them no cohort tools (review F1).
 */
export function managesInEngine(dashboard: SpaceTransformationDashboard | null): boolean {
  return !!dashboard && (dashboard.audience !== "learner" || dashboard.isAdmin);
}
